'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { Article } from '@/types/article';
import { getArticleUrl } from '@/lib/slugs';
import { useSiteLanguage } from '@/lib/SiteLanguageContext';

interface NewsTickerProps {
    articles: Article[];
}

function getCategoryLink(article: Article): string {
    // For 'Vijesti', link to the all-articles page
    if (article.kategorija === 'Vijesti') return '/svi-clanci';

    // If podpodkategorija exists, navigate to podpodkategorija page
    if (article.podpodkategorija) {
        // Build URL from kategorija + podkategorija + podpodkategorija
        const categoryMap: Record<string, Record<string, Record<string, string>>> = {
            'Naoružanje': {
                'Kopnene snage': {
                    'Tenkovi i oklopna vozila': '/articles/weapons/army/tanks',
                    'Artiljerija': '/articles/weapons/army/artillery',
                },
                'Vazduhoplovstvo i PVO': {
                    'Avioni i helikopteri': '/articles/weapons/airforce/aircraft',
                    'PVO sistemi i raketno naoružanje': '/articles/weapons/airforce/air-defense',
                    'Bespilotne letjelice': '/articles/weapons/airforce/drones',
                },
                'Mornarica': {
                    'Površinska plovila': '/articles/weapons/navy/surface-vessels',
                    'Podmornica': '/articles/weapons/navy/submarines',
                    'Mornaričko naoružanje': '/articles/weapons/navy/naval-weapons',
                },
            },
            'Municija': {
                'Pješadijska municija': {
                    'Pješadijska municija': '/articles/ammunition/infantry',
                },
                'Minobacačka municija': {
                    'Minobacačka municija': '/articles/ammunition/mortar',
                },
                'Klasič­na artiljerijska municija': {
                    'Klasična artiljerijska municija': '/articles/ammunition/artillery',
                },
                'Raketna artiljerijska municija': {
                    'Raketna artiljerijska municija': '/articles/ammunition/rocket-artillery',
                },
                'Ručne bombe': {
                    'Ručne bombe': '/articles/ammunition/grenades',
                },
                'Mine za ručne bacače': {
                    'Mine za ručne bacače': '/articles/ammunition/mines-hand',
                },
                'Ručni raketni bacači': {
                    'Ručni raketni bacači': '/articles/ammunition/rocket-launchers',
                },
                'Municija za bestrzajne topove': {
                    'Municija za bestrzajne topove': '/articles/ammunition/recoilless',
                },
                'Balistika': {
                    'Balistika': '/articles/ammunition/ballistics',
                },
                'Savremena municija': {
                    'Savremena municija': '/articles/ammunition/modern',
                },
                'Eksplozivne materije': {
                    'Eksplozivne materije': '/articles/ammunition/explosives',
                },
                'Elementi municije': {
                    'Elementi municije': '/articles/ammunition/elements',
                },
                'Pirotehnika': {
                    'Pirotehnika': '/articles/ammunition/pyrotechnics',
                },
                'Upaljači': {
                    'Upaljači': '/articles/ammunition/igniters',
                },
            },
        };

        const link = categoryMap[article.kategorija]?.[article.podkategorija]?.[article.podpodkategorija];
        if (link) return link;
    }

    // If no podpodkategorija, navigate to podkategorija page
    if (article.podkategorija) {
        const podkategoryMap: Record<string, Record<string, string>> = {
            'Naoružanje': {
                'Kopnene snage': '/articles/weapons/army',
                'Vazduhoplovstvo i PVO': '/articles/weapons/airforce',
                'Mornarica': '/articles/weapons/navy',
                'Strateške snage': '/articles/weapons/strategic',
                'Savremeno naoružanje': '/articles/weapons/modern',
            },
            'Municija': {
                'Pješadijska municija': '/articles/ammunition/infantry',
                'Minobacačka municija': '/articles/ammunition/mortar',
                'Klasična artiljerijska municija': '/articles/ammunition/artillery',
                'Raketna artiljerijska municija': '/articles/ammunition/rocket-artillery',
                'Ručne bombe': '/articles/ammunition/grenades',
                'Mine za ručne bacače': '/articles/ammunition/mines-hand',
                'Ručni raketni bacači': '/articles/ammunition/rocket-launchers',
                'Municija za bestrzajne topove': '/articles/ammunition/recoilless',
                'Balistika': '/articles/ammunition/ballistics',
                'Savremena municija': '/articles/ammunition/modern',
                'Eksplozivne materije': '/articles/ammunition/explosives',
                'Elementi municije': '/articles/ammunition/elements',
                'Pirotehnika': '/articles/ammunition/pyrotechnics',
                'Upaljači': '/articles/ammunition/igniters',
            },
            'Analitika': {
                'Zemlje bivše Jugoslavije': '/articles/analytics/yugoslavia',
                'Evropa': '/articles/analytics/europe',
                'NATO': '/articles/analytics/nato',
                'Rusija': '/articles/analytics/russia',
                'Bliski istok': '/articles/analytics/middle-east',
                'Rat u Ukrajini': '/articles/analytics/ukraine',
            },
            'Namjenska preduzeća': {
                'Istorija razvoja': '/articles/enterprises/history',
                'Proizvodni kapaciteti': '/articles/enterprises/production',
                'Stari jugoslovenski projekti': '/articles/enterprises/yugoslav-projects',
                'Modernizacija i izvozni program': '/articles/enterprises/modernization',
            },
            'Vojna istorija': {
                'Vojske kroz vijekove': '/articles/history/vojske-kroz-vijekove',
                'I svjetski rat': '/articles/history/wwi',
                'II svjetski rat': '/articles/history/wwii',
                'Hladni rat': '/articles/history/cold-war',
            },

        };

        const link = podkategoryMap[article.kategorija]?.[article.podkategorija];
        if (link) return link;
    }

    // Fallback to category
    return `/articles/${article.kategorija?.toLowerCase().replace(/\s+/g, '-')}`;
}

function getTimeAgo(dateString: string, lang: 'sr' | 'en' = 'sr'): string {
    const parts = dateString.split('.');
    if (parts.length >= 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const articleDate = new Date(year, month, day);
        const now = new Date();
        const diffMs = now.getTime() - articleDate.getTime();
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (lang === 'en') {
            if (diffHours < 1) return 'just now';
            if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
            if (diffDays === 1) return '1 day ago';
            if (diffDays < 7) return `${diffDays} days ago`;
            return dateString;
        }

        if (diffHours < 1) return 'upravo sada';
        if (diffHours < 24) return `pre ${diffHours} ${diffHours === 1 ? 'sat' : diffHours < 5 ? 'sata' : 'sati'}`;
        if (diffDays === 1) return 'pre 1 dan';
        if (diffDays < 7) return `pre ${diffDays} dana`;
        return dateString;
    }
    return dateString;
}

const categoryTranslations: Record<string, string> = {
    'Vijesti': 'News',
    'Naoružanje': 'Armament', 'Municija': 'Ammunition',
    'Namjenska preduzeća': 'Defense Enterprises', 'Vojna istorija': 'Military History',
    'Kopnene snage': 'Ground Forces', 'Vazduhoplovstvo i PVO': 'Air Force & Air Defense',
    'Mornarica': 'Navy', 'Strateške snage': 'Strategic Forces',
    'Naoružanje Trećeg rajha': 'Third Reich Armaments', 'Naoružanje bivše JNA': 'Former JNA Armaments',
    'Savremeno naoružanje': 'Modern Armaments', 'Konstrukcija naoružanja': 'Weapon Construction',
    'Tenkovi i oklopna vozila': 'Tanks & Armored Vehicles', 'Artiljerija': 'Artillery',
    'Pješadijska municija': 'Infantry Ammunition', 'Minobacačka municija': 'Mortar Ammunition',
    'Klasična artiljerijska municija': 'Classic Artillery Ammunition',
    'Raketna artiljerijska municija': 'Rocket Artillery Ammunition',
    'Vodjene rakete': 'Guided Missiles', 'Ručne bombe': 'Hand Grenades',
    'Mine za ručne bacače': 'Grenades for Handheld Launchers',
    'Municija za bestrzajne topove': 'Recoilless Cannon Ammunition',
    'Balistika': 'Ballistics', 'Savremena municija': 'Modern Ammunition',
    'Eksplozivne materije': 'Explosives', 'Elementi municije': 'Ammunition Elements',
    'Pirotehnika': 'Pyrotechnics', 'Upaljači': 'Fuzes',
    'Istorija razvoja': 'History of Development', 'Proizvodni kapaciteti': 'Production Capacity',
    'Stari jugoslovenski projekti': 'Old Yugoslav Projects',
    'Modernizacija i izvozni program': 'Modernization & Export Program',
    'Vojske kroz vijekove': 'Armies Through the Ages',
    'I svjetski rat': 'World War I', 'II svjetski rat': 'World War II',
    'Hladni rat': 'Cold War', 'Poznati konstruktori': 'Famous Designers',
    'Nekategorisano': 'Uncategorized',
};

export default function NewsTicker({ articles }: NewsTickerProps) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const { siteLang } = useSiteLanguage();
    const latestArticles = articles.slice(0, 3);

    // Reset index when articles list changes (e.g. language switch)
    useEffect(() => {
        setCurrentIndex(0);
    }, [articles]);

    const nextArticle = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % latestArticles.length);
    }, [latestArticles.length]);

    // Auto-rotate every 4 seconds
    useEffect(() => {
        if (latestArticles.length === 0) return;

        const interval = setInterval(nextArticle, 4000);
        return () => clearInterval(interval);
    }, [latestArticles.length, nextArticle]);

    if (latestArticles.length === 0) return null;

    const currentArticle = latestArticles[currentIndex] ?? latestArticles[0];

    if (!currentArticle) return null;

    return (
        <div className="fixed top-16 left-0 right-0 z-40 flex items-center justify-between py-2 px-4 lg:px-[calc(1rem+180px)] bg-[rgb(27,34,49)]">
            <div className="flex items-center gap-4 flex-1 min-w-0">
                {/* NAJNOVIJE Badge */}
                <div className="flex items-center gap-2 flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span className="text-white font-bold text-sm uppercase tracking-wide">{siteLang === 'en' ? 'LATEST' : 'NAJNOVIJE'}</span>
                </div>

                {/* News Headline - Clickable */}
                <Link
                    href={getArticleUrl(currentArticle)}
                    className="text-white text-sm hover:text-gray-300 transition-colors truncate flex-1 cursor-pointer"
                >
                    {currentArticle.naslov}
                </Link>

                {/* Time Ago */}
                <span className="text-gray-400 text-sm flex-shrink-0 hidden sm:block">
                    {getTimeAgo(currentArticle.datum, siteLang)}
                </span>
            </div>

            {/* Category Badge - Clickable */}
            <Link
                href={getCategoryLink(currentArticle)}
                className="ml-4 px-3 py-1 text-xs font-semibold text-white rounded flex-shrink-0 hover:opacity-80 transition-opacity"
                style={{ backgroundColor: '#b91c1c' }}
            >
                {(() => {
                    const name = currentArticle.kategorija === 'Vijesti'
                        ? currentArticle.kategorija
                        : (currentArticle.podpodkategorija || currentArticle.podkategorija || currentArticle.kategorija);
                    return siteLang === 'en' ? (categoryTranslations[name] ?? name) : name;
                })()}
            </Link>
        </div>
    );
}